import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { ConnectionDot } from '../App.js';
import { useStore } from '../lib/store.js';
import { api } from '../lib/api.js';
import type { ServerDoc, ServerLiveStats } from '@panel/shared';
import CommandPalette from './CommandPalette.js';
import { NotificationBell, ToastStack } from './NotificationCenter.js';
import RingGauge from './RingGauge.js';
import { useBranding } from '../lib/branding.js';
import {
  IconBackup,
  IconBulk,
  IconChevronRight,
  IconClose,
  IconDashboard,
  IconLogo,
  IconLogout,
  IconMenu,
  IconPlus,
  IconSearch,
  IconServer,
  IconSettings,
  IconUpload,
} from './Icons.js';

interface User { discord_id: string; discord_username: string; role: string; avatar_url?: string }

const EXPANDED_KEY = 'sidebar.expandedMachines';

function loadExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

export default function Layout({ user, children }: { user: User; children: ReactNode }) {
  const machines = useStore((s) => s.machines);
  const nodeStatus = useStore((s) => s.nodeStatus);
  const servers = useStore((s) => s.servers);
  const liveStats = useStore((s) => s.liveStats);
  const [expanded, setExpanded] = useState<Set<string>>(loadExpanded);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const branding = useBranding();
  const location = useLocation();

  // Close the mobile drawer on route change so users aren't stranded after
  // tapping a link.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Cmd+K / Ctrl+K opens the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const machineNames = Array.from(new Set([...nodeStatus.keys(), ...Array.from(servers.values()).map((s) => s.Server_Machine)])).sort();

  const toggle = (name: string) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(name)) next.delete(name); else next.add(name);
      try { localStorage.setItem(EXPANDED_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  // Pre-bucket servers by machine for the dropdown render
  const serversByMachine = new Map<string, ServerDoc[]>();
  for (const s of servers.values()) {
    const list = serversByMachine.get(s.Server_Machine) ?? [];
    list.push(s);
    serversByMachine.set(s.Server_Machine, list);
  }
  for (const list of serversByMachine.values()) list.sort((a, b) => a.Server_Name.localeCompare(b.Server_Name));

  return (
    <div className="h-full flex bg-bg">
      {/* Mobile scrim */}
      {mobileOpen && (
        <div
          aria-hidden
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-30 bg-bg/70 backdrop-blur-sm lg:hidden animate-fade-in"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-full w-72 lg:w-64 shrink-0 border-r border-bg-border bg-bg-raised flex flex-col
                    transition-transform duration-200 ease-out
                    ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <div className="px-5 h-14 border-b border-bg-border flex items-center gap-2.5">
          <BrandLogo url={branding.logo_url} size={28} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-fg leading-none tracking-tight truncate" title={branding.name}>{branding.name}</div>
            <div className="text-[10px] text-fg-dim mt-1 truncate" title={branding.tagline}>{branding.tagline || 'v2.0'}</div>
          </div>
          <button
            type="button"
            className="lg:hidden btn-icon"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <IconClose />
          </button>
        </div>

        <nav className="px-3 py-3 space-y-0.5 text-sm">
          <NavItem to="/" icon={<IconDashboard />} label="Dashboard" />
          <NavItem to="/backups" icon={<IconBackup />} label="Backups" />
          <NavItem to="/files" icon={<IconUpload />} label="Files" />
          {user.role !== 'viewer' && <NavItem to="/bulk" icon={<IconBulk />} label="Bulk Actions" />}
          {user.role !== 'viewer' && <NavItem to="/servers/new" icon={<IconPlus />} label="New Server" />}
        </nav>

        <div className="px-3 mt-2 flex-1 min-h-0 flex flex-col">
          <div className="px-2 mb-1.5 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-fg-dim font-semibold">Machines</span>
            <span className="text-[10px] text-fg-dim tabular-nums">{machineNames.length}</span>
          </div>
          <div className="space-y-0.5 text-sm overflow-y-auto pb-2 -mr-1 pr-1">
            {machineNames.map((name) => {
              const online = !!nodeStatus.get(name)?.online;
              const tel = machines.get(name);
              const isExpanded = expanded.has(name);
              const childServers = serversByMachine.get(name) ?? [];
              return (
                <div key={name}>
                  <div className="group flex items-stretch rounded-md hover:bg-bg-card transition-colors">
                    <button
                      type="button"
                      onClick={() => toggle(name)}
                      className="flex items-center justify-center w-7 text-fg-dim hover:text-fg shrink-0 cursor-pointer"
                      title={isExpanded ? 'Collapse' : 'Expand'}
                      aria-label={isExpanded ? `Collapse ${name}` : `Expand ${name}`}
                    >
                      <IconChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    <NavLink
                      to={`/machines/${encodeURIComponent(name)}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2 flex-1 min-w-0 pr-2.5 py-1.5 rounded-md font-medium ${
                          isActive ? 'text-fg' : 'text-fg-muted group-hover:text-fg'
                        }`
                      }
                    >
                      <span className="relative shrink-0 text-fg-dim group-hover:text-fg-muted">
                        <IconServer className="w-4 h-4" />
                        <span
                          className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ring-2 ring-bg-raised ${
                            online ? 'bg-status-online' : 'bg-status-error'
                          }`}
                          title={online ? 'Online' : 'Machine unreachable — agent disconnected'}
                        />
                      </span>
                      <span className="truncate flex-1">{name}</span>
                      {online && tel && (
                        <RingGauge
                          value={tel.cpu.load_pct}
                          size={14}
                          stroke={2.25}
                          silent
                          title={`CPU ${tel.cpu.load_pct.toFixed(0)}%`}
                        />
                      )}
                    </NavLink>
                  </div>
                  {isExpanded && childServers.length > 0 && (
                    <div className="ml-2 mt-0.5 space-y-0.5">
                      {childServers.map((s, i) => (
                        <ServerRow
                          key={s.Server_ID}
                          server={s}
                          live={liveStats.get(s.Server_ID)}
                          isLast={i === childServers.length - 1}
                          machineOnline={online}
                        />
                      ))}
                    </div>
                  )}
                  {isExpanded && childServers.length === 0 && (
                    <div className="ml-7 py-1 text-[11px] text-fg-dim italic">No servers</div>
                  )}
                </div>
              );
            })}
            {machineNames.length === 0 && (
              <div className="px-2 py-3 text-[11px] text-fg-dim italic">No machines yet</div>
            )}
          </div>
        </div>

        <div className="p-3 border-t border-bg-border flex items-center gap-2 text-xs">
          <UserAvatar user={user} size={32} />

          <div className="flex-1 min-w-0">
            <div className="truncate font-medium text-fg">{user.discord_username}</div>
            <div className="text-fg-dim capitalize">{user.role}</div>
          </div>
          <Link to="/settings" className="btn-icon" title="Settings" aria-label="Settings">
            <IconSettings />
          </Link>
          <button
            type="button"
            onClick={() => api.post('/api/auth/logout').then(() => window.location.reload())}
            className="btn-icon hover:!text-status-error"
            title="Logout"
            aria-label="Logout"
          >
            <IconLogout />
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar onOpenMenu={() => setMobileOpen(true)} onOpenPalette={() => setPaletteOpen(true)} />
        <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-5 lg:py-7">
          <div className="max-w-[1400px] mx-auto">{children}</div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ToastStack />
    </div>
  );
}

/** Map URL segments to nicely-cased display labels. Underscored / numeric
 *  segments (machine names, server IDs) pass through. */
const SEGMENT_LABELS: Record<string, string> = {
  servers:  'Servers',
  machines: 'Machines',
  backups:  'Backups',
  files:    'Files',
  bulk:     'Bulk Actions',
  settings: 'Settings',
  new:      'New',
};

function prettySegment(seg: string): string {
  const decoded = decodeURIComponent(seg);
  return SEGMENT_LABELS[decoded.toLowerCase()] ?? decoded;
}

function Topbar({ onOpenMenu, onOpenPalette }: { onOpenMenu: () => void; onOpenPalette: () => void }) {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
  const modKey = isMac ? '⌘' : 'Ctrl';
  return (
    <header className="h-14 px-4 sm:px-6 border-b border-bg-border bg-bg-raised/80 backdrop-blur-md flex items-center justify-between gap-3 sticky top-0 z-20">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <button
          type="button"
          onClick={onOpenMenu}
          className="lg:hidden btn-icon"
          aria-label="Open menu"
        >
          <IconMenu />
        </button>
        <nav className="flex items-center gap-1.5 text-sm text-fg-muted truncate min-w-0" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-fg transition-colors">Home</Link>
          {segments.map((seg, i) => (
            <span key={i} className="flex items-center gap-1.5 min-w-0">
              <span className="text-fg-dim">/</span>
              <span className={`truncate ${i === segments.length - 1 ? 'text-fg' : 'hover:text-fg'}`}>
                {prettySegment(seg)}
              </span>
            </span>
          ))}
        </nav>
      </div>
      <div className="flex items-center gap-2 text-xs text-fg-muted shrink-0">
        <button
          type="button"
          onClick={onOpenPalette}
          className="hidden sm:inline-flex items-center gap-2 h-8 pl-2.5 pr-1.5 rounded-lg bg-bg-card border border-bg-border hover:border-bg-ring hover:text-fg cursor-pointer transition-colors"
          aria-label="Open command palette"
          title="Search (⌘K / Ctrl+K)"
        >
          <IconSearch className="w-3.5 h-3.5 text-fg-dim" />
          <span className="text-fg-dim">Search…</span>
          <span className="kbd ml-1">{modKey}K</span>
        </button>
        <button
          type="button"
          onClick={onOpenPalette}
          className="sm:hidden btn-icon"
          aria-label="Search"
        >
          <IconSearch />
        </button>
        <NotificationBell />
        <span className="w-px h-5 bg-bg-border mx-1 hidden sm:block" />
        <ConnectionDot />
        <span className="hidden md:inline">Live</span>
      </div>
    </header>
  );
}

function NavItem({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `flex items-center gap-3 px-2.5 py-2 rounded-md text-sm transition-colors duration-150 ${
          isActive
            ? 'bg-bg-card text-fg shadow-sm border border-bg-border/60'
            : 'text-fg-muted hover:bg-bg-card/60 hover:text-fg border border-transparent'
        }`
      }
    >
      <span className="text-fg-muted [.bg-bg-card_&]:text-accent">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

/**
 * Avatar that gracefully falls back to a colored monogram if the Discord CDN
 * URL fails or the user has no avatar set (default Discord avatars are not
 * stored on the user doc).
 */
function UserAvatar({ user, size = 28 }: { user: User; size?: number }) {
  const [failed, setFailed] = useState(false);
  const initial = (user.discord_username[0] ?? '?').toUpperCase();
  const src = user.avatar_url && !failed ? user.avatar_url : null;
  return (
    <div
      className="rounded-full bg-bg-card border border-bg-border flex items-center justify-center text-fg font-medium overflow-hidden shrink-0"
      style={{ width: size, height: size }}
      aria-hidden
    >
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span style={{ fontSize: size * 0.4 }}>{initial}</span>
      )}
    </div>
  );
}

/** Renders the configured logo URL or falls back to the built-in IconLogo.
 *  Image errors silently fall back so a broken URL never blocks the chrome.
 *  `failed` is reset whenever `url` changes — otherwise a transient earlier
 *  failure would keep hiding the logo even after the operator uploads a fix. */
function BrandLogo({ url, size = 28 }: { url?: string; size?: number }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [url]);
  if (url && !failed) {
    return (
      <img
        key={url}
        src={url}
        alt=""
        className="rounded-md object-cover shrink-0 bg-bg-card border border-bg-border"
        style={{ width: size, height: size }}
        onError={() => setFailed(true)}
      />
    );
  }
  return <IconLogo size={size} />;
}

function ServerRow({
  server,
  live,
  isLast,
  machineOnline = true,
}: {
  server: ServerDoc;
  live: ServerLiveStats | undefined;
  isLast: boolean;
  machineOnline?: boolean;
}) {
  const status = live?.status ?? 'offline';
  // When the agent is unreachable, any cached live stats are stale — we
  // genuinely don't know what the server is doing. Drop the dot to grey
  // ("unknown") rather than continuing to show online/restarting, and
  // hide the player count for the same reason.
  const dotClass = !machineOnline
    ? 'bg-status-offline'
    : status === 'online' ? 'bg-status-online'
      : status === 'restarting' ? 'bg-status-updating'
      : 'bg-status-offline';
  const players = live?.rcon?.Players ?? null;
  const maxPlayers = live?.rcon?.MaxPlayers ?? null;
  const playerText =
    machineOnline && status === 'online' && players != null && maxPlayers != null
      ? `${players}/${maxPlayers}`
      : '—';

  return (
    <NavLink
      to={`/servers/${server.Server_ID}`}
      className={({ isActive }) =>
        `flex items-center gap-1 pl-2 pr-2.5 py-1 rounded-md text-[12px] transition-colors duration-100 ${
          isActive ? 'bg-bg-card text-fg' : 'text-fg-muted hover:bg-bg-card hover:text-fg'
        } ${machineOnline ? '' : 'opacity-60'}`
      }
      title={machineOnline ? undefined : 'Machine unreachable — server state unknown'}
    >
      <span className="text-fg-dim font-mono select-none w-3 text-center" aria-hidden>
        {isLast ? '└' : '├'}
      </span>
      <span className={`dot ${dotClass} shrink-0`} />
      <span className="truncate flex-1">{server.Server_Name}</span>
      <span className="text-[10px] text-fg-dim tabular-nums shrink-0">{playerText}</span>
    </NavLink>
  );
}
